import {
  getTimestampFormat,
  LeakDetectorReading,
  SensorType,
  useSensorEvents,
  useHumidityChartData,
  HumidityThresholds,
  HumidityLevel,
} from 'common';
import { useMemo } from 'react';
import { tokens } from 'tamagui-config';

import { useGetSensorRollupByType } from '../sensors/useGetSensorRollupByType';

export interface HumidityDataItem {
  value: number;
  label: string;
  error?: boolean;
  timestamp?: number;
}

const getHumidityStatus = (value: number): string => {
  for (const [key, threshold] of Object.entries(HumidityThresholds)) {
    const t = threshold as { low: number; high: number };
    if (value >= t.low && value <= t.high) {
      switch (key) {
        case 'dry':
          return 'Dry';
        case 'normal':
          return 'Normal';
        case 'slightly_humid':
          return 'Slightly Humid';
        case 'humid':
          return 'Humid';
        case 'very_humid':
          return 'Very Humid';
        default:
          return 'Normal';
      }
    }
  }
  return 'Normal';
};

const HUMIDITY_COLORS = {
  [HumidityLevel.Dry]: {
    color: tokens.color.humidityGlowWarning,
    gradient: {
      start: tokens.color.humidityGradientWarningStart,
      end: tokens.color.humidityGradientWarningEnd,
    },
  },
  [HumidityLevel.Normal]: {
    color: tokens.color.waterGradientStart,
    gradient: { start: tokens.color.waterGradientStart, end: tokens.color.waterGradientEnd },
  },
  [HumidityLevel.SlightlyHumid]: {
    color: tokens.color.humidityGlowWarning,
    gradient: {
      start: tokens.color.humidityGradientWarningStart,
      end: tokens.color.humidityGradientWarningEnd,
    },
  },
  [HumidityLevel.Humid]: {
    color: tokens.color.humidityGlowCritical,
    gradient: {
      start: tokens.color.humidityGradientCriticalStart,
      end: tokens.color.humidityGradientCriticalEnd,
    },
  },
  [HumidityLevel.VeryHumid]: {
    color: tokens.color.humidityGlowCritical,
    gradient: {
      start: tokens.color.humidityGradientCriticalStart,
      end: tokens.color.humidityGradientCriticalEnd,
    },
  },
};

const getColorForValue = (value: number | null) => {
  if (value === null || value === undefined) return tokens.color.humidityGlowWarning;
  for (const [key, threshold] of Object.entries(HumidityThresholds)) {
    const t = threshold as { low: number; high: number };
    if (value >= t.low && value <= t.high) {
      return HUMIDITY_COLORS[key as keyof typeof HUMIDITY_COLORS].color;
    }
  }
  return tokens.color.humidityGlowWarning;
};

const getGradientForValue = (value: number | null) => {
  if (value === null || value === undefined)
    return {
      start: tokens.color.humidityGradientWarningStart,
      end: tokens.color.humidityGradientWarningEnd,
    };
  for (const [key, threshold] of Object.entries(HumidityThresholds)) {
    const t = threshold as { low: number; high: number };
    if (value >= t.low && value <= t.high) {
      return HUMIDITY_COLORS[key as keyof typeof HUMIDITY_COLORS].gradient;
    }
  }
  return {
    start: tokens.color.humidityGradientWarningStart,
    end: tokens.color.humidityGradientWarningEnd,
  };
};

const HIGH_HUMIDITY_THRESHOLD = 61;
export const useHumidity = (sensorId: string) => {
  const { sensor } = useGetSensorRollupByType<LeakDetectorReading>(SensorType.LEAK_DETECTOR, 7);

  // Use the new common humidity chart data hook with the same logic as mobile
  const {
    data: humidityChartData,
    latestValue: latestHumidityValue,
    loading: inProgress,
    error,
  } = useHumidityChartData({
    sensorId,
    hubId: sensor?.hubId,
    days: 7,
    enabled: !!sensorId,
  });

  // Convert the common chart data format to the legacy HumidityDataItem format for backward compatibility
  const humidityData: HumidityDataItem[] = useMemo(
    () =>
      humidityChartData.map((item) => ({
        value: item.value,
        label: item.label,
        error: item.error ?? item.value >= HIGH_HUMIDITY_THRESHOLD,
        timestamp: item.timestamp,
      })),
    [humidityChartData],
  );

  const humidityStatus =
    latestHumidityValue !== null ? getHumidityStatus(latestHumidityValue) : null;

  // Get latest sensor event for lastReported timestamp
  const { sensorEvents } = useSensorEvents(sensor?.hubId ?? undefined, sensorId);
  const latestEvent = useMemo(
    () =>
      sensorEvents.find(
        (event) =>
          event.eventType === 'DATA' &&
          event.parsedMetadata?.measurementType === SensorType.LEAK_DETECTOR,
      ),
    [sensorEvents],
  );

  const lastReported = latestEvent?.timestamp
    ? getTimestampFormat(latestEvent.timestamp * 1000)
    : null;

  return {
    humidityData,
    loading: inProgress,
    error,
    latestHumidityValue,
    humidityStatus,
    lastReported,
    getColorForValue,
    getGradientForValue,
  };
};
